/**
 * Auth Redux Slice - Authentication and user session management
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

interface User {
  id: string;
  email: string;
  username: string;
  displayName: string;
  avatar?: string;
  role: 'user' | 'developer' | 'admin';
  verified: boolean;
  subscription?: {
    plan: string;
    status: string;
    currentPeriodEnd: string;
  };
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
  loginModalOpen: boolean;
  signupModalOpen: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  refreshToken: null,
  loading: false,
  error: null,
  loginModalOpen: false,
  signupModalOpen: false
};

// Async Thunks
interface AuthTokenResponse {
  user: User;
  token: string;
  refreshToken: string;
}

interface RefreshTokenResponse {
  token: string;
  refreshToken: string;
}

export const login = createAsyncThunk<
  AuthTokenResponse,
  { email: string; password: string },
  { rejectValue: string }
>(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await apiService.post<AuthTokenResponse>('/auth/login', credentials);
      if (response.status === 'success' && response.data) {
        const { user, token, refreshToken } = response.data;

        // Store tokens
        localStorage.setItem('auth_token', token);
        localStorage.setItem('refresh_token', refreshToken);
        apiService.setAuthToken(token);

        return { user, token, refreshToken };
      }
      throw new Error('Login failed');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Login failed');
    }
  }
);

export const signup = createAsyncThunk<
  AuthTokenResponse,
  {
    email: string;
    password: string;
    username: string;
    displayName: string;
  },
  { rejectValue: string }
>(
  'auth/signup',
  async (data: {
    email: string;
    password: string;
    username: string;
    displayName: string;
  }, { rejectWithValue }) => {
    try {
      const response = await apiService.post<AuthTokenResponse>('/auth/signup', data);
      if (response.status === 'success' && response.data) {
        const { user, token, refreshToken } = response.data;

        // Store tokens
        localStorage.setItem('auth_token', token);
        localStorage.setItem('refresh_token', refreshToken);
        apiService.setAuthToken(token);

        return { user, token, refreshToken };
      }
      throw new Error('Signup failed');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Signup failed');
    }
  }
);

export const refreshTokenThunk = createAsyncThunk<
  RefreshTokenResponse,
  void,
  { rejectValue: string }
  >(
  'auth/refreshToken',
  async (_unused, { rejectWithValue, getState }) => {
    void _unused;
    try {
      const state = getState() as { auth: AuthState };
      const refreshToken = state.auth.refreshToken || localStorage.getItem('refresh_token');

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiService.post<RefreshTokenResponse>('/auth/refresh', { refreshToken });
      if (response.status === 'success' && response.data) {
        const { token, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem('auth_token', token);
        localStorage.setItem('refresh_token', newRefreshToken);
        apiService.setAuthToken(token);

        return { token, refreshToken: newRefreshToken };
      }
      throw new Error('Token refresh failed');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Token refresh failed');
    }
  }
);

export const loadUserProfile = createAsyncThunk<User, void, { rejectValue: string }>(
  'auth/loadUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get<User>('/auth/profile');
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load profile');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load profile');
    }
  }
);

export const updateProfile = createAsyncThunk<User, Partial<User>, { rejectValue: string }>(
  'auth/updateProfile',
  async (updates: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await apiService.patch<User>('/auth/profile', updates);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to update profile');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update profile');
    }
  }
);

export const logout = createAsyncThunk<null, void, { rejectValue: string }>(
  'auth/logout',
  async () => {
    try {
      // Call logout endpoint
      await apiService.post('/auth/logout');

      // Clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      apiService.setAuthToken(null);

      return null;
    } catch (error) {
      // Even if API call fails, clear local data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      apiService.setAuthToken(null);

      return null;
    }
  }
);

// Check for existing session on app load
export const initializeAuth = createAsyncThunk<AuthTokenResponse | null, void, { rejectValue: string }>(
  'auth/initialize',
  async (_unused, { dispatch, rejectWithValue }) => {
    void _unused;
    try {
      const token = localStorage.getItem('auth_token');
      const refreshToken = localStorage.getItem('refresh_token');

      if (token && refreshToken) {
        apiService.setAuthToken(token);

        // Try to load user profile
        const profileResponse = await apiService.get<User>('/auth/profile');
        if (profileResponse.status === 'success' && profileResponse.data) {
          return {
            user: profileResponse.data,
            token,
            refreshToken
          };
        }

        // Token might be expired, try refresh, then reload profile
        const refreshed = await dispatch(refreshTokenThunk()).unwrap();
        const refreshedProfile = await apiService.get<User>('/auth/profile');
        if (refreshedProfile.status === 'success' && refreshedProfile.data) {
          return {
            user: refreshedProfile.data,
            token: refreshed.token,
            refreshToken: refreshed.refreshToken
          };
        }
      }

      return null;
    } catch (error) {
      // Clear invalid tokens
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      apiService.setAuthToken(null);

      return rejectWithValue(error instanceof Error ? error.message : 'Initialization failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    openLoginModal: (state) => {
      state.loginModalOpen = true;
      state.signupModalOpen = false;
    },

    closeLoginModal: (state) => {
      state.loginModalOpen = false;
    },

    openSignupModal: (state) => {
      state.signupModalOpen = true;
      state.loginModalOpen = false;
    },

    closeSignupModal: (state) => {
      state.signupModalOpen = false;
    },

    closeAuthModals: (state) => {
      state.loginModalOpen = false;
      state.signupModalOpen = false;
    }
  },
  extraReducers: (builder) => {
    // Initialize Auth
    builder
      .addCase(initializeAuth.fulfilled, (state, action) => {
        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
        }
        state.loading = false;
      });

    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.loginModalOpen = false;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Signup
    builder
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.signupModalOpen = false;
        state.error = null;
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Refresh Token
    builder
      .addCase(refreshTokenThunk.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
      })
      .addCase(refreshTokenThunk.rejected, (state) => {
        // Token refresh failed, log out user
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      });

    // Load User Profile
    builder
      .addCase(loadUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      });

    // Update Profile
    builder
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      });

    // Logout
    builder
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.error = null;
      });
  }
});

export const {
  clearError,
  openLoginModal,
  closeLoginModal,
  openSignupModal,
  closeSignupModal,
  closeAuthModals
} = authSlice.actions;

export default authSlice.reducer;
